import React from "react";
import {
  NavLink,
  useLocation,
  useHistory,
} from "react-router-dom";
import { Breadcrumbs, Link as MuiLink } from '@material-ui/core';
import PropTypes from 'prop-types';
import Loading from "./loader";

function Nav(props) {
  const location = useLocation();
  const history = useHistory();

  // Маппинг маршрута главной страницы
  const routeLabels = {
    '/': 'Главная'
  };

  // Функция для получения метки страницы
  const getRouteLabel = (path) => {
    if (path === '/') return 'Главная';
    if (path.includes('settings')) return 'Настройки';
    if (path.includes('password_change')) return 'Смена пароля';
    if (path.includes('voteDocs')) return 'Материалы собрания';
    if (path.includes('voteResults')) return 'Итоговый протокол';
    if (path.includes('voteExtended')) return 'Описание собрания';
    if (path.includes('voteStream')) return 'Трансляция собрания';
    if (path.includes('voteActive')) return 'Голосование';
    if (path.includes('voteOneResult')) return 'Результаты голосования';
    if (path.includes('voteUsers')) return 'Участники собрания';
    if (path.includes('voteAllUsers')) return 'Список пользователей';
    if (path.includes('voteMeetings')) return 'Собрания';
    if (path.includes('voteUserMeetings')) return 'Собрания пользователя';
    if (path.includes('voteMeetingCreate')) return 'Создание собрания';
    if (path.includes('voteMeetingRedactDocs')) return 'Материалы собрания';
    if (path.includes('voteAgendaCreate')) return 'Создание повестки';
    if (path.includes('voteSave')) return 'Сохранение';
    if (path.includes('voteRegUsers')) return 'Зарегистрированные пользователи';
    if (path.includes('voteMeetingRedact')) return 'Редактирование собрания';
    if (path.includes('voteAgendaRedact')) return 'Редактирование повестки';
    if (path.includes('voteAgendaPreShow')) return 'Предпросмотр бюллетеня';

    return 'Неизвестная страница';
  };

  const loadBreadcrumbs = () => {
    const saved = localStorage.getItem('breadcrumbs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Ошибка при парсинге breadcrumbs из localStorage', e);
      }
    }
    return [];
  };

  const saveBreadcrumbs = (breadcrumbs) => {
    localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));
  };

  // Хранение истории хлебных крошек
  const [breadcrumbHistory, setBreadcrumbHistory] = React.useState(() => loadBreadcrumbs());

  React.useEffect(() => {
    saveBreadcrumbs(breadcrumbHistory);
  }, [breadcrumbHistory]);

  // Следим за изменением маршрута и обновляем историю
  React.useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath !== '/') {
      setBreadcrumbHistory(prev => {
        // Проверяем, является ли текущий путь voteExtended
        const isVoteExtended = /^\/voteExtended\/\d+\/\d+$/.test(currentPath);
        let currentMeetingId = null;
        let currentAccountId = null;

        if (isVoteExtended) {
          const parts = currentPath.split('/');
          currentMeetingId = parts[2];
          currentAccountId = parts[3];
        }

        // Если это voteExtended — проверяем, совпадает ли meetingId или accountId с предыдущим
        if (isVoteExtended && prev.length > 0) {
          const lastItem = prev[prev.length - 1];
          const isLastVoteExtended = /^\/voteExtended\/\d+\/\d+$/.test(lastItem.path);

          if (isLastVoteExtended) {
            const lastParts = lastItem.path.split('/');
            const lastMeetingId = lastParts[2];
            const lastAccountId = lastParts[3];

            // Если это тот же пакет — обновляем историю как обычно
            if (lastMeetingId === currentMeetingId && lastAccountId === currentAccountId) {
              return prev;
            }
          } else {
            // Если последняя крошка НЕ voteExtended — полностью заменяем историю
            return [
              { path: '/', label: routeLabels['/'] },
              { path: currentPath, label: getRouteLabel(currentPath) }
            ];
          }
        }

        // Если это специальные пути — очищаем историю и начинаем заново
        const specialPaths = ['/voteMeetings', '/voteMeetingCreate', '/voteAllUsers', '/voteExtended'];
        const isSpecialPath = specialPaths.some(path => currentPath.startsWith(path));

        if (isSpecialPath) {
          return [
            { path: '/', label: routeLabels['/'] },
            { path: currentPath, label: getRouteLabel(currentPath) }
          ];
        }

        // Если текущий путь уже есть в истории — обрезаем до него
        const index = prev.findIndex(item => item.path === currentPath);
        if (index !== -1) {
          return prev.slice(0, index + 1);
        }

        // Если это voteExtended и в истории уже есть другой voteExtended — заменяем его
        if (isVoteExtended) {
          const hasVoteExtended = prev.some(item =>
            /^\/voteExtended\/\d+\/\d+$/.test(item.path)
          );
          if (hasVoteExtended) {
            return [...prev.slice(0, -1), { path: currentPath, label: getRouteLabel(currentPath) }];
          }
        }

        // Всегда добавляем Главную, если её нет
        if (!prev.some(item => item.path === '/')) {
          return [
            { path: '/', label: routeLabels['/'] },
            { path: currentPath, label: getRouteLabel(currentPath) }
          ];
        }

        // Иначе просто добавляем новый путь
        return [...prev, { path: currentPath, label: getRouteLabel(currentPath) }];
      });
    }
  }, [location]);

  // Функция генерации хлебных крошек
  const generateBreadcrumbs = () => {
    const currentPath = location.pathname;

    if (currentPath === '/') {
      return null;
    }

    return (
      <Breadcrumbs aria-label="breadcrumb">
        {breadcrumbHistory.map((item, index) => {
          const isClickable = !item.path.includes('password_change');

          return (
            <span key={index}>
              {isClickable ? (
                <MuiLink
                  color="inherit"
                  component={NavLink}
                  to={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBreadcrumbClick(item.path);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {item.label}
                </MuiLink>
              ) : (
                <span style={{ color: '#777', pointerEvents: 'none' }}>
                  {item.label}
                </span>
              )}
            </span>
          );
        })}
      </Breadcrumbs>
    );
  };

  const handleBreadcrumbClick = (to) => {
    history.push(to);
  };

  // Показываем хлебные крошки только если не на главной странице
  const showBreadcrumbs = location.pathname !== '/';

  return (
    <div>
      <div className="background">
        <div>
          <button id="logoutBttn" onClick={props.handle_logout} className="button button1">Выйти</button>
        </div>
        <div className="header">
          <h2 className="changeableHeading">электронное голосование</h2>
          <div style={{ textAlign: 'right' }} className="changeableText">
            {props.is_staff || props.is_superuser
              ? `Администратор (${props.personname})`
              : props.personname
            }
          </div>
        </div>
      </div>

      <ul className="ul">
        <li className="li"><NavLink to="/">Главная</NavLink></li>
        <li className="li"><NavLink to="/settings">Настройки</NavLink></li>
      </ul>

      {showBreadcrumbs && (
        <div className="sticky-breadcrumbs">
          <div style={{ marginLeft: '120px' }}>
            {generateBreadcrumbs()}
          </div>
        </div>
      )}

      {/* Контент страницы передаётся как children */}
      {props.isFetchingAfterLogin ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          width: '100%'
        }}>
          <Loading />
        </div>
      ) : (
        <div className="content">
          {props.children}
        </div>
      )}
    </div>
  );
}

Nav.propTypes = {
  logged_in: PropTypes.bool.isRequired,
  handle_logout: PropTypes.func.isRequired,
  personname: PropTypes.string,
  is_staff: PropTypes.bool,
  is_superuser: PropTypes.bool,
  isFetchingAfterLogin: PropTypes.bool,
};

export default Nav;